import React, { useState, useEffect } from "react";
import { Button, Input, Row, Col, message } from "antd";
import TableContainer from "../../components/TableContainer";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { scanBarcode, closeHubReconciliation } from "../../actionsMethods/hubReconciliationMethods";
import { APIs, getToken, getPremiseId } from "../../utils/worker";

const StartReconciliation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reconcileId = location.state?.reconcileId || searchParams.get("reconcileId");
  const [barcode, setBarcode] = useState("");
  const [scannedData, setScannedData] = useState([]);
  const [currentBarcode, setCurrentBarcode] = useState("");

  const {
    result: scanResponseData,
    error: scanError,
  } = useSelector((state) => state.hubReconciliationReducer.scanBarcode);

  const {
    result: closeResponseData,
    error: closeError,
  } = useSelector((state) => state.hubReconciliationReducer.closeHubReconciliation);

  const fetchInProgressShipments = async () => {
    try {
      const tokenStr = getToken();
      const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;
      const premiseIdStr = getPremiseId();
      const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;
      const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userId = userInfoData?.userInfo?.userId || "";

      const response = await axios.get(
        `${APIs.baseURL}/hub-reconciliation-service/v1/hub-reconcile/shipments/${reconcileId}?type=IN_PROGRESS`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "SMCS-PREMISE-ID": premiseId,
            "USER-ID": userId,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data?.data || response.data || [];
        const items = Array.isArray(responseData) ? responseData : (Array.isArray(responseData.data) ? responseData.data : []);

        const mappedData = items.reverse().map((item, index) => ({
          id: index + 1,
          barcode: item.awbNumber || item.barcode || "N/A",
          time: item.scannedAt || item.scannedTime || item.createdAt || "",
          status: item.status || "AVAILABLE",
        }));

        setScannedData(mappedData);
      }
    } catch (error) {
      console.error("Error fetching in-progress shipments", error);
    }
  };

  useEffect(() => {
    if (reconcileId) {
      fetchInProgressShipments();
    }
  }, [reconcileId]);

  const handleScan = () => {
    if (barcode.trim()) {
      const scannedCode = barcode.trim();

      const isDuplicate = scannedData.some(item => item.barcode === scannedCode);
      if (isDuplicate) {
        message.warning(`Barcode ${scannedCode} has already been scanned`, 1);
        setBarcode("");
        return;
      }

      setCurrentBarcode(scannedCode);

      const tokenStr = getToken();
      const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

      const premiseIdStr = getPremiseId();
      const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;

      const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userId = userInfoData?.userInfo?.userId || "";

      dispatch(scanBarcode({
        reconcileId,
        awbNumber: scannedCode,
        token,
        premiseId,
        userId,
      }));

      setBarcode(""); // Clear input early for fast scanning
    }
  };

  useEffect(() => {
    if (scanResponseData && currentBarcode) {
      const resData = scanResponseData?.data?.[0] || {};
      const status = resData.status || "AVAILABLE";

      setScannedData((prev) => {
        // Prevent duplicate addition if effect runs twice
        if (prev.some(item => item.barcode === currentBarcode)) return prev;
        const newList = [
          { barcode: currentBarcode, time: new Date().toLocaleTimeString(), status },
          ...prev,
        ];
        return newList.map((item, index) => ({ ...item, id: index + 1 }));
      });
      message.success(`Barcode ${currentBarcode} scanned successfully`, 1);
      setCurrentBarcode(""); // Reset
    } else if (scanError && currentBarcode) {
      message.error(`Failed to scan barcode ${currentBarcode}`, 1);
      setCurrentBarcode(""); // Reset
    }
  }, [scanError, scanResponseData]); // Note: currentBarcode is not in dependency array to avoid triggering

  const handleCloseReconciliation = () => {
    if (!reconcileId) {
      navigate(-1);
      return;
    }

    const tokenStr = getToken();
    const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

    const premiseIdStr = getPremiseId();
    const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;

    const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
    const userId = userInfoData?.userInfo?.userId || "";

    dispatch(closeHubReconciliation({
      reconcileId,
      token,
      premiseId,
      userId,
    }));
  };

  useEffect(() => {
    if (closeResponseData) {
      message.success("Reconciliation closed successfully", 1);
      navigate("/hub-reconciliation");
    } else if (closeError) {
      message.error("Failed to close Hub Reconciliation", 1);
    }
  }, [closeResponseData, closeError]);

  const columns = [
    { title: "S.No", dataIndex: "id", key: "id" },
    { title: "Barcode Number", dataIndex: "barcode", key: "barcode" },
    { title: "Status", dataIndex: "status", key: "status" },
  ];

  return (
    <div className="dashboard-container" style={{ padding: "20px" }}>
      <div className="align-center space-between" style={{ marginBottom: 16 }}>
        <div className="pl-1-5 fw-600" style={{ fontSize: 20 }}>
          Start Reconciliation
        </div>
      </div>

      <div className="master-main-container view-all-staff">
        <div style={{ marginBottom: 20, padding: "1.5rem 1.5rem 0" }}>
          <Row gutter={[16, 16]} className="filters-section" justify="space-between" align="bottom">
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="filter-label" style={{ marginBottom: "8px", fontWeight: 500 }}>Barcode Scan</div>
              <Input
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Enter Shipment or Bag number to Scan"
                bordered
                style={{
                  borderRadius: "5px",
                  height: "40px",
                }}
                suffix={false}
                onKeyPress={(e) => {
                  const forbiddenChars = /[@!$#%^&*=+()""';?.><]/;
                  if (forbiddenChars.test(e.key)) {
                    e.preventDefault();
                    message.destroy();
                  }
                }}
                onPressEnter={handleScan}
                autoFocus
              />
            </Col>
            <Col>
              <Button onClick={handleCloseReconciliation} className="save-btn" type="primary" danger style={{ height: "40px", width: "120px", borderRadius: "5px" }}>
                Close
              </Button>
            </Col>
          </Row>
        </div>

        <TableContainer
          className="table-head-sticky"
          dataSource={scannedData}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content", y: "calc(100vh - 400px)" }}
        />
      </div>
    </div>
  );
};

export default StartReconciliation;
